import React, { Fragment } from 'react';

import Issue24Icon from '@atlaskit/icon-object/glyph/issue/24';
import PageIcon24 from '@atlaskit/icon-object/glyph/page/24';
import {
	type ActivityItemEdge,
	is1PActivity,
	isDocumentActivity,
	isGraphActivity,
} from '@atlassian/search-client/recent-activities/types';

import { type ProductKeys } from '../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { EventTypeMessage } from '../../../../common/ui/event-type-message';
import { ProductIcon } from '../../../../common/ui/product-icon';
import { type SearchLinkProps } from '../../../../common/ui/search-link/types';
import { getProductFromProviderId } from '../../../../common/utils/search-result-utils';

import { ConfluenceResultMeta } from './confluence-result-meta';

const CONTEXT_PATH = '/wiki/spaces';

export const recentViewsResultMapper: (
	edge: ActivityItemEdge,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => SearchLinkProps = (edge, thirdPartyConfigs) => {
	const item = edge.node;
	const iconLabel = `${item.object.product} ${item.object.type}`;

	if (is1PActivity(item.object)) {
		switch (item.object.product) {
			case 'jira':
				return {
					title:
						`${item.object.data?.key}: ${
							(item.object.data?.fieldsById?.edges || [{}])[0].node?.text
						}` || '',
					description: `Jira ${item.object.type}`,
					subheading: (
						<EventTypeMessage
							timestamp={item.event.timestamp}
							eventType={item.event.eventType}
							isMyActivityData={true}
						/>
					),
					icon: <Issue24Icon label={iconLabel} />,
					onClick: () => {},
					navigationUrl: item.object.data?.webUrl || '',
				};
			case 'confluence':
				return {
					title: item.object.data?.title || '',
					description: (
						<ConfluenceResultMeta
							resultContentType={item.object.type}
							resultContentSubtype={item.object.data?.subtype}
							resultAncestors={item.object.data?.ancestors}
							resultSpace={item.object.data?.space}
						/>
					),
					subheading: (
						<EventTypeMessage
							timestamp={item.event.timestamp}
							eventType={item.event.eventType}
							isMyActivityData={true}
						/>
					),
					icon: <PageIcon24 label={iconLabel} />,
					onClick: () => {},
					navigationUrl: `${item.object.data?.links?.base || CONTEXT_PATH}${
						item.object.data?.links?.webUi || ''
					}`,
				};
			case 'townsquare':
				return {
					title: `${item.object.data?.key}: ${item.object.data?.name}` || '',
					description: `Atlas ${item.object.type}`,
					subheading: (
						<EventTypeMessage
							timestamp={item.event.timestamp}
							eventType={item.event.eventType}
							isMyActivityData={true}
						/>
					),
					icon: (
						<img
							width={24}
							height={24}
							src={item.object.data?.iconData || ''}
							alt={`${item.object.product} ${item.object.type}`}
						></img>
					),
					onClick: () => {},
					navigationUrl: item.object.data?.url || '',
					meta: `Atlas ${item.object.type}`,
				};
			default:
				return {
					title: '',
					icon: <Fragment />,
					onClick: () => {
						return;
					},
				};
		}
	} else if (isGraphActivity(item.object)) {
		if (isDocumentActivity(item.object)) {
			const providerId = item.object.data?.provider?.providerId;
			const product = getProductFromProviderId(thirdPartyConfigs, providerId);

			return {
				title: `${item.object.data?.displayName}` || '',
				description: `${product} ${item.object.data?.type?.category}`,
				subheading: (
					<EventTypeMessage
						timestamp={item.event.timestamp}
						eventType={item.event.eventType}
						isMyActivityData={true}
					/>
				),
				icon: <ProductIcon size="small" product={product as ProductKeys} />,
				onClick: () => {},
				navigationUrl: item.object.data?.url || '',
			};
		} else {
			return {
				title: '',
				icon: <Fragment />,
				onClick: () => {
					return;
				},
			};
		}
	} else {
		return {
			title: '',
			icon: <Fragment />,
			onClick: () => {
				return;
			},
		};
	}
};
